package no.vareflow.app.domain

/**
 * Password hashing for local test-mode accounts.
 *
 * Local accounts used to be stored as readable text. They are now stored as salted
 * PBKDF2-HMAC-SHA256 hashes in the form `pbkdf2$<iterations>$<salt>$<hash>` (Base64, no padding).
 * Legacy plain-text values are still accepted on login and are migrated to hashes at startup.
 * Firebase accounts never store a password locally (the field stays blank).
 * Phone and browser produce and accept the same text.
 */
object LocalPasswords {
    private const val PREFIX = "pbkdf2"
    private const val ITERATIONS = 60_000
    private const val SALT_BYTES = 16

    fun isHashed(stored: String): Boolean = stored.startsWith("$PREFIX$")

    fun hash(plain: String): String {
        val salt = secureRandomBytes(SALT_BYTES)
        val derived = derivePasswordKey(plain, salt, ITERATIONS)
        return listOf(PREFIX, ITERATIONS.toString(), Crypto.base64Encode(salt), Crypto.base64Encode(derived))
            .joinToString("$")
    }

    /** Hashes [value] unless it is blank (cloud account) or already a hash. */
    fun ensureHashed(value: String): String =
        if (value.isBlank() || isHashed(value)) value else hash(value)

    fun verify(plain: String, stored: String): Boolean {
        if (stored.isBlank() || plain.isEmpty()) return false
        if (!isHashed(stored)) {
            // Legacy plain-text value; compared in constant time and migrated at next startup.
            return Crypto.constantTimeEquals(plain.encodeToByteArray(), stored.encodeToByteArray())
        }
        val parts = stored.split('$')
        if (parts.size != 4) return false
        val iterations = parts[1].toIntOrNull()?.takeIf { it > 0 } ?: return false
        val salt = Crypto.base64Decode(parts[2]) ?: return false
        val expected = Crypto.base64Decode(parts[3]) ?: return false
        return Crypto.constantTimeEquals(derivePasswordKey(plain, salt, iterations), expected)
    }
}
